import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { COLORS } from "../../common/colors";
import type { ComplianceOrg } from "src/providers/compliance/complianceTypes";
import { COMPLIANCE_COLORS } from "./complianceColors";

interface OrgSidebarProps {
  orgs: ComplianceOrg[];
  selectedOrgId: string | null;
  onOrgSelect: (id: string) => void;
}

const GROUPS: { key: ComplianceOrg["category"]; label: string }[] = [
  { key: "pharma", label: "Pharma partners" },
  { key: "health_system", label: "Health systems" },
  { key: "other", label: "Other" }
];

function OrgItem({
  org,
  selected,
  onClick
}: {
  org: ComplianceOrg;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        px: 2,
        py: "9px",
        cursor: "pointer",
        borderLeft: selected
          ? `3px solid ${COLORS.PRIMARY_1000}`
          : "3px solid transparent",
        backgroundColor: selected ? COLORS.PRIMARY_200 : "transparent",
        "&:hover": {
          backgroundColor: selected ? COLORS.PRIMARY_200 : "#f9fbff"
        }
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: selected ? COLORS.PRIMARY_1000 : COLORS.GREY_900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.25
          }}
        >
          {org.displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: COLORS.GREY_700,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            mt: "1px",
            lineHeight: 1.35
          }}
        >
          {org.projects.length} project{org.projects.length !== 1 ? "s" : ""} ·{" "}
          {org.totalPatients} patients
        </Typography>
      </Box>
    </Box>
  );
}

function SidebarGroup({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: "10px",
          cursor: "pointer",
          "&:hover": { backgroundColor: COMPLIANCE_COLORS.HOVER_BG }
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            color: COLORS.GREY_800
          }}
        >
          {label}
        </Typography>
        {open ? (
          <ExpandLess sx={{ fontSize: 18, color: COLORS.GREY_500 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18, color: COLORS.GREY_500 }} />
        )}
      </Box>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
}

export function OrgSidebar({
  orgs,
  selectedOrgId,
  onOrgSelect
}: OrgSidebarProps): JSX.Element {
  return (
    <Box
      sx={{
        width: 246,
        minWidth: 246,
        borderRight: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
        backgroundColor: "#ffffff",
        overflowY: "auto",
        height: "100%"
      }}
    >
      <Box sx={{ px: 2, py: "14px" }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: COLORS.GREY_700
          }}
        >
          Organizations
        </Typography>
      </Box>
      {GROUPS.map((group) => {
        const groupOrgs = orgs.filter((o) => o.category === group.key);
        if (groupOrgs.length === 0) return null;
        return (
          <SidebarGroup key={group.key} label={group.label}>
            {groupOrgs.map((org) => (
              <OrgItem
                key={org.id}
                org={org}
                selected={org.id === selectedOrgId}
                onClick={() => onOrgSelect(org.id)}
              />
            ))}
          </SidebarGroup>
        );
      })}
    </Box>
  );
}
